<script>
import MarkdownEditor from "../common/MarkdownEditor.vue";
import CharacterCounter from "../common/CharacterCounter.vue";
import { formatDate } from "@/utils/date";

export default {
  name: "PostsEdit",
  components: {
    MarkdownEditor,
    CharacterCounter,
  },
  data: function () {
    return {
      form: {
        id: 0,
        title: "",
        description: "",
        content: "",
        category_id: 0,
        status: 0,
        publish_at: null,
        created_at: "",
        updated_at: "",
      },
      categoryList: [],
    };
  },
  computed: {
    titleLength() {
      return this.form.title ? this.form.title.length : 0;
    },
    descLength() {
      return this.form.description ? this.form.description.length : 0;
    },
    isEdit() {
      // 判断是否为编辑模式：有路由参数id且form.id不为0
      return this.$route.params.id && this.form.id && this.form.id !== 0;
    },
    submitButtonText() {
      return this.isEdit ? "更新文章" : "立即创建";
    },
  },
  methods: {
    async onSubmit() {
      // 表单验证
      if (!this.validateForm()) {
        return;
      }

      let loading = null;

      try {
        // 显示加载状态
        loading = this.$loading({
          lock: true,
          text: this.isEdit ? "更新中..." : "保存中...",
          spinner: "el-icon-loading",
          background: "rgba(0, 0, 0, 0.7)",
        });

        // 准备提交数据
        const submitData = {
          title: this.form.title.trim(),
          description: this.form.description.trim(),
          content: this.form.content,
          category_id: this.form.category_id,
          status: this.form.status,
          publish_at: this.form.publish_at
            ? formatDate(this.form.publish_at)
            : "",
        };

        // 如果是编辑模式，添加文章ID
        if (this.isEdit) {
          submitData.id = this.form.id;
        }

        // 根据是否为编辑模式调用不同的API
        if (this.isEdit) {
          await this.$api.postsUpdate(submitData);
        } else {
          await this.$api.postsStore(submitData);
        }

        // 关闭加载状态
        loading.close();

        // 显示成功消息
        this.$message({
          type: "success",
          message: this.isEdit ? "文章更新成功！" : "文章创建成功！",
        });

        // 返回后台文章列表
        this.$router.push("/admin/posts");
      } catch (error) {
        // 关闭加载状态
        if (loading) {
          loading.close();
        }

        // 处理错误信息
        let errorMessage = this.isEdit
          ? "更新失败，请重试"
          : "保存失败，请重试";

        if (error.response && error.response.data) {
          if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.errors) {
            // 处理验证错误
            const errors = error.response.data.errors;
            const firstError = Object.values(errors)[0];
            if (Array.isArray(firstError) && firstError.length > 0) {
              errorMessage = firstError[0];
            }
          }
        }

        // 显示错误消息
        this.$message({
          type: "error",
          message: errorMessage,
        });

        console.error("保存文章失败:", error);
      }
    },

    goBack() {
      // 返回后台文章列表
      this.$router.push("/admin/posts");
    },

    validateForm() {
      // 验证标题
      if (!this.form.title) {
        this.$message({
          type: "warning",
          message: "请输入文章标题",
        });
        return false;
      }

      if (this.form.title.trim().length > 100) {
        this.$message({
          type: "warning",
          message: "标题不能超过100个字符",
        });
        return false;
      }

      if (this.form.description.trim().length > 150) {
        this.$message({
          type: "warning",
          message: "简介不能超过150个字符",
        });
        return false;
      }

      // 验证内容
      if (!this.form.content || !this.form.content.trim()) {
        this.$message({
          type: "warning",
          message: "请输入文章内容",
        });
        return false;
      }

      // 验证分类
      if (!this.form.category_id || this.form.category_id === 0) {
        this.$message({
          type: "warning",
          message: "请选择文章分类",
        });
        return false;
      }

      // 验证发布时间
      if (this.form.status === 2 && !this.form.publish_at) {
        this.$message({
          type: "warning",
          message: "请选择发布时间",
        });
        return false;
      }

      return true;
    },

    handleStatusChange(val) {
      if (val !== 2) {
        this.form.publish_at = null;
      }
    },
  },
  mounted: function () {
    // 加载分类列表
    this.$api.categorySelect().then((response) => {
      this.categoryList = response.data;
    });

    // 如果有路由参数id，说明是编辑模式，加载文章数据
    const postId = this.$route.params.id;
    if (postId && postId !== "new") {
      this.$api
        .postsShow({ id: postId })
        .then((response) => {
          this.form = response.data;
          if (this.form.publish_at) {
            this.form.publish_at = new Date(
              this.form.publish_at.replace(/-/g, "/")
            );
          }
        })
        .catch((error) => {
          console.error("加载文章失败:", error);
          this.$message({
            type: "error",
            message: "加载文章失败，请重试",
          });
        });
    }
    // 如果是新增模式（路由参数为'new'或没有参数），使用默认的空表单
  },
};
</script>

<template>
  <div>
    <el-form ref="form" :model="form" label-width="80px">
      <el-form-item label="标题">
        <el-input
          v-model="form.title"
          maxlength="100"
          show-word-limit
        ></el-input>
        <CharacterCounter :current="titleLength" :max="100" />
      </el-form-item>
      <el-form-item label="简介">
        <el-input
          v-model="form.description"
          maxlength="150"
          show-word-limit
        ></el-input>
        <CharacterCounter :current="descLength" :max="150" />
      </el-form-item>
      <el-form-item label="内容">
        <MarkdownEditor
          v-model="form.content"
          :height="400"
          placeholder="请输入文章内容..."
        />
      </el-form-item>
      <el-form-item label="分类">
        <el-select v-model="form.category_id" placeholder="请选择">
          <el-option
            v-for="item in categoryList"
            :key="item.name"
            :label="item.name"
            :value="item.id"
          >
          </el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select
          v-model="form.status"
          placeholder="请选择"
          @change="handleStatusChange"
        >
          <el-option label="待发布" :value="0"></el-option>
          <el-option label="已发布" :value="1"></el-option>
          <el-option label="定时发布" :value="2"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="发布时间" v-if="form.status === 2">
        <el-date-picker
          v-model="form.publish_at"
          type="datetime"
          placeholder="选择日期时间"
        >
        </el-date-picker>
      </el-form-item>
      <el-form-item class="submit-btn-group">
        <el-button type="primary" @click="onSubmit">{{
          submitButtonText
        }}</el-button>
        <el-button @click="goBack">返回</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<style scoped>
/* Utilize ::v-deep to penetrate child components (Element UI) */
::v-deep .el-form-item__content {
  text-align: left !important;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: normal; /* Reset line-height to avoid weird spacing */
}

/* Handle the button group specifically to keep them in a row */
.submit-btn-group ::v-deep .el-form-item__content {
  flex-direction: row;
  align-items: center;
}

/* Title and Desc inputs */
::v-deep .el-form-item .el-input {
  max-width: 500px;
  /* text-align: left; - inherited */
}

::v-deep .el-form-item .el-input .el-input__inner {
  text-align: left !important;
}

/* Category select */
::v-deep .el-form-item .el-select {
  text-align: left !important;
  width: 200px;
  max-width: 200px;
}

::v-deep .el-form-item .el-select .el-input {
  width: 100%; /* Fill the select container */
}

/* Dropdown items left align */
::v-deep .el-select-dropdown .el-select-dropdown__item {
  text-align: left !important;
}

/* Markdown editor container */
::v-deep .el-form-item .markdown-editor {
  max-width: 100%;
  width: 100%;
}

/* Buttons margin */
::v-deep .el-button + .el-button {
  margin-left: 10px;
}

/* Character counter alignment */
.character-counter {
  align-self: flex-start;
  margin-top: 5px;
}
</style>
